import {QuestionnaireStep} from 'APIs/letrusAPI/questionnaire/types';
import {Button, useModals} from 'letrus-ui';
import GeneralErrorModal from 'components/GeneralErrorModal';
import {useEffect, useState} from 'react';
import {
  fetchQuestionAttemptRequest,
  sendAiTutorAttemptRequest,
} from 'APIs/letrusAPI/questionnaire';
import {useMobile, useQueryParam} from 'utils/hooks';
import wavingHappily from 'assets/images/characters/CarolinaMariadeJesus/waving-happily.webp';
import LoadingQuestion from 'components/LoadingComponents/LoadingQuestion';
import {ChatResponse} from 'APIs/aiTutorAPI/chat/types';
import styles from './AiTutorQuestion.module.scss';
import AiTutorChat from '../AiTutorChat';
import AiTutorReport from '../AiTutorReport';

type QuestionStage = 'initial' | 'chat' | 'report';

export interface QuestionProps {
  onAdvance?(): void;
  learningPathStepId?: number;
  prevQuestionAnswer?: string;
  question?: QuestionnaireStep['question'];
}

function AiTutorQuestion({
  question,
  onAdvance,
  learningPathStepId,
  prevQuestionAnswer,
}: QuestionProps) {
  const [error, setError] = useState<string>();
  const [stage, setStage] = useState<QuestionStage>('initial');
  const [report, setReport] = useState<ChatResponse['report']>();
  const [finishedSessionId, setFinishedSessionId] = useState<number>();

  const {isMobile} = useMobile();
  const queryParams = useQueryParam();
  const learningPathUserInstanceId = queryParams.get('trilha') ?? '';

  const finishQuestion = sendAiTutorAttemptRequest();
  const questionAttempt = fetchQuestionAttemptRequest(
    {
      step: learningPathStepId,
      question: question?.id,
      learning_path_user_instance: learningPathUserInstanceId,
    },
    {
      isPaused: () => !learningPathStepId,
      onSuccess: ({results}) =>
        setFinishedSessionId(results[0].ai_tutor_conversation_id),
    },
  );

  const hasQuestionAnswer = !!questionAttempt.data?.count;

  const modals = useModals(['error']);

  useEffect(() => {
    if (error) {
      modals.error?.showModal();
    }
  }, [error]);

  function handleNextStep() {
    if (report && stage !== 'report') {
      setStage('report');
      return;
    }

    if (stage === 'initial') {
      setStage('chat');
      return;
    }

    if (finishedSessionId && !hasQuestionAnswer) {
      finishQuestion.trigger(
        {
          question_id: question?.id,
          step_id: learningPathStepId,
          source: isMobile ? 'app' : 'web',
          ai_tutor_conversation_id: finishedSessionId,
          learning_path_user_instance_id: learningPathUserInstanceId,
        },
        {
          onSuccess: onAdvance,
          onError: () => setError('Atividade indisponível'),
        },
      );
    } else {
      onAdvance?.();
    }
  }

  return (
    <article className={styles.wrapper}>
      {questionAttempt.isLoading || !question ? (
        <LoadingQuestion questionType="ai_tutor" />
      ) : (
        <>
          {stage === 'initial' && (
            <div className={styles.emptyState}>
              <img src={wavingHappily} alt="Tutor acenando" height={250} />
              <h2>{question.settings?.initial_title}</h2>
              <p>{question.settings?.initial_message}</p>
            </div>
          )}

          {stage === 'chat' && (
            <AiTutorChat
              questionId={question.id}
              settings={question.settings}
              finishedSessionId={finishedSessionId}
              prevQuestionAnswer={prevQuestionAnswer}
              onError={(error) => setError(error)}
              onComplete={(sessionId, report) => {
                setReport(report);
                setFinishedSessionId(sessionId);
              }}
            />
          )}

          {stage === 'report' && (
            <AiTutorReport
              report={report}
              sessionId={finishedSessionId}
              title={question?.settings?.report_title}
              body={question?.settings?.report_body}
            />
          )}
        </>
      )}

      <div className={styles.controls}>
        <Button
          type="submit"
          variant="primary"
          data-testid="submit-button"
          onClick={() => handleNextStep()}
          disabled={stage === 'chat' && !finishedSessionId}
        >
          {stage !== 'chat' ? question?.button_text : 'Avançar'}
        </Button>
      </div>

      <GeneralErrorModal
        id="error"
        title="Ops!"
        showIcon={false}
        subTitle={error}
        description="Tente novamente mais tarde"
      />
    </article>
  );
}

export default AiTutorQuestion;
